'use client';

import { useState, type SyntheticEvent } from 'react';

import { AnimatePresence, motion } from 'motion/react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { SCANNER_COPY } from '@/lib/content';
import { EMAIL_REGEX } from '@/lib/validation';

import { ClearButton, FormFieldError } from './shared';

/**
 * The gate between asking for a scan and running one. It hands the address back rather than
 * posting it: the scan itself submits the lead, so the report goes out with real scores attached
 * instead of arriving twice.
 */
export function ScanEmailModal({
  open,
  domain,
  onClose,
  onConfirm,
}: {
  open: boolean;
  domain: string;
  onClose: () => void;
  onConfirm: (email: string) => void;
}) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [wasOpen, setWasOpen] = useState(open);

  // Reopening after a first scan should start clean, not show the last address or its error.
  if (open && !wasOpen) {
    setWasOpen(true);
    setEmail('');
    setError(undefined);
  } else if (!open && wasOpen) {
    setWasOpen(false);
  }

  function submit(event: SyntheticEvent) {
    event.preventDefault();
    const value = email.trim();
    if (!value) {
      setError(SCANNER_COPY.emailRequiredError);
      return;
    }
    if (!EMAIL_REGEX.test(value)) {
      setError(SCANNER_COPY.emailInvalidError);
      return;
    }
    setError(undefined);
    onConfirm(value);
  }

  return (
    <Dialog open={open} onOpenChange={value => !value && onClose()}>
      <DialogContent className="sm:max-w-md">
        <AnimatePresence mode="wait">
          <motion.div
            key="form"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex min-h-0 flex-1 flex-col"
          >
            <DialogHeader className="shrink-0">
              <DialogTitle className="text-2xl">{SCANNER_COPY.emailModalTitle}</DialogTitle>
              <DialogDescription>
                {SCANNER_COPY.emailModalDescriptionPrefix}
                <span className="font-mono text-foreground">{domain}</span>
                {SCANNER_COPY.emailModalDescriptionSuffix}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} noValidate className="mt-4 flex min-h-0 flex-1 flex-col">
              <div className="min-h-0 flex-1">
                <label htmlFor="scan-email" className="mb-1.5 block text-sm font-medium">
                  {SCANNER_COPY.emailLabel} <span className="text-destructive">*</span>
                </label>
                <div className="relative">
                  <Input
                    id="scan-email"
                    name="email"
                    type="email"
                    autoFocus
                    required
                    placeholder={SCANNER_COPY.emailPlaceholder}
                    value={email}
                    aria-invalid={!!error}
                    aria-describedby={error ? 'scan-email-error' : undefined}
                    onChange={event => {
                      setEmail(event.target.value);
                      setError(undefined);
                    }}
                    className={email ? 'pr-8' : ''}
                  />
                  {email && (
                    <ClearButton
                      onClick={() => {
                        setEmail('');
                        setError(undefined);
                      }}
                    />
                  )}
                </div>
                <FormFieldError id="scan-email-error" error={error} />
                <p className="mt-3 text-sm text-muted-foreground">{SCANNER_COPY.emailConsent}</p>
              </div>

              <div className="shrink-0 pt-4">
                <Button type="submit" className="w-full">
                  {SCANNER_COPY.emailModalSubmit}
                </Button>
              </div>
            </form>
          </motion.div>
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
